import { Activity, AlertOctagon, Ban, Cloud, CloudOff, Database, Download, FileUp, GitMerge, RefreshCw, RotateCcw, Trash2, Wifi } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { apiRequest } from '../api/client'
import { normalizeEquipment } from '../api/normalize'
import type { Equipment } from '../api/types'
import { useSync } from '../offline/SyncContext'
import { APP_BUILD, deviceId, shellVersion } from '../offline/diagnostics'
import { clearOutbox, discardOutboxItem, resolveEquipmentConflict, retryOutboxItem } from '../offline/outbox'
import type { OutboxRecord } from '../offline/db'
import { useAuth } from '../auth/AuthContext'
import { useToast } from '../components/ToastProvider'
import { Badge, Button, ConfirmDialog, EmptyState, LoadingState, Modal, statusTone } from '../components/ui'
import { errorMessage, formatDate, formatNumber, formatRelativeTime } from '../utils/format'

/**
 * Подсказка по установке, когда браузер не показывает кнопку: iPhone — через «Поделиться»; Huawei и Honor без сервисов
 * Google — через меню браузера Huawei «Добавить на рабочий экран» (кнопки «Установить» там нет, приложение работает и так,
 * в том числе без сети); остальные Android — меню браузера или Chrome.
 */
export function installHint(userAgent: string): string {
  if (/iPhone|iPad|iPod/i.test(userAgent)) return 'iPhone: в Safari нажмите «Поделиться» и выберите «На экран „Домой“».'
  if (/HuaweiBrowser|HarmonyOS|HONOR|HUAWEI/i.test(userAgent)) return 'Huawei и Honor: в браузере Huawei откройте меню (≡) и выберите «Добавить на рабочий экран» — кнопки «Установить» в нём нет. Ярлык открывает приложение на весь экран, оно работает и без установки, в том числе без сети. Если на телефоне есть Chrome, установить можно через его меню (⋮) → «Установить приложение».'
  return 'Android: в меню браузера (⋮) выберите «Установить приложение» или «Добавить на главный экран». Если пункта нет, откройте адрес в Chrome.'
}

interface InstallPromptEvent extends Event {
  prompt(): Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

export function SyncPage() {
  const sync = useSync()
  const toast = useToast()
  const { user } = useAuth()
  const [usage, setUsage] = useState<{ usage: number; quota: number } | null>(null)
  const [shellBuild, setShellBuild] = useState<string | null>(null)
  const [reporting, setReporting] = useState(false)
  const [clearingObsolete, setClearingObsolete] = useState(false)
  const activeQueue = useMemo(() => sync.queue.filter((item) => item.state !== 'obsolete'), [sync.queue])
  const obsoleteQueue = useMemo(() => sync.queue.filter((item) => item.state === 'obsolete'), [sync.queue])
  const [installPrompt, setInstallPrompt] = useState<InstallPromptEvent | null>(null)
  const [installed, setInstalled] = useState(window.matchMedia('(display-mode: standalone)').matches)
  const [installing, setInstalling] = useState(false)
  // Сохранена ли офлайн-оболочка: без неё установленное приложение открывается пустым экраном при отсутствии сети.
  const [shell, setShell] = useState<'ready' | 'missing' | 'unsupported' | null>(null)
  const [manualSyncing, setManualSyncing] = useState(false)
  const [discardCandidate, setDiscardCandidate] = useState<OutboxRecord | null>(null)
  const [discardingId, setDiscardingId] = useState<string | null>(null)
  const [resolving, setResolving] = useState<OutboxRecord | null>(null)

  useEffect(() => {
    void shellVersion().then(setShellBuild)
    void navigator.storage?.estimate().then((estimate) => setUsage({ usage: estimate.usage || 0, quota: estimate.quota || 0 }))
    const prompt = (event: Event) => { event.preventDefault(); setInstallPrompt(event as InstallPromptEvent) }
    const appInstalled = () => { setInstalled(true); setInstallPrompt(null) }
    window.addEventListener('beforeinstallprompt', prompt)
    window.addEventListener('appinstalled', appInstalled)
    let cancelled = false
    const checkShell = () => {
      if (!('serviceWorker' in navigator)) { setShell('unsupported'); return }
      void navigator.serviceWorker.getRegistration().then((registration) => { if (!cancelled) setShell(registration?.active ? 'ready' : 'missing') }).catch(() => { if (!cancelled) setShell('missing') })
    }
    checkShell()
    const shellTimer = window.setInterval(checkShell, 5_000)
    return () => { cancelled = true; window.clearInterval(shellTimer); window.removeEventListener('beforeinstallprompt', prompt); window.removeEventListener('appinstalled', appInstalled) }
  }, [])

  async function install() {
    if (!installPrompt) return
    setInstalling(true)
    try {
      await installPrompt.prompt()
      const choice = await installPrompt.userChoice
      if (choice.outcome === 'accepted') {
        setInstalled(true)
        toast.show('Приложение установлено на устройстве.', 'success')
      } else {
        toast.show('Установка отменена. При необходимости установите приложение через меню устройства.', 'info')
      }
      setInstallPrompt(null)
    } catch {
      setInstallPrompt(null)
      toast.show('Не удалось открыть установку. Выберите «Установить приложение» в меню устройства.', 'error')
    } finally {
      setInstalling(false)
    }
  }

  async function sendStatusReport() {
    setReporting(true)
    try {
      const accepted = await sync.sendReport(true)
      toast.show(accepted ? 'Отчёт о состоянии устройства отправлен администратору.' : 'Отчёт не отправлен. Проверьте подключение и попробуйте снова.', accepted ? 'success' : 'error')
    } finally {
      setReporting(false)
    }
  }

  async function clearObsolete() {
    if (!user) return
    setClearingObsolete(true)
    try {
      const removed = await clearOutbox(user.id, 'obsolete')
      await sync.refreshQueue()
      toast.show(removed ? `Удалено отклонённых действий: ${removed}.` : 'Отклонённых действий уже нет.', 'info')
    } finally {
      setClearingObsolete(false)
    }
  }

  async function sendSavedChanges() {
    setManualSyncing(true)
    try {
      const result = await sync.syncNow()
      if (!result) {
        toast.show('Изменения не отправлены. Проверьте подключение и войдите в систему снова.', 'error')
      } else if (result.attention > 0) {
        toast.show(`Отправлено: ${result.succeeded}. Требуют решения: ${result.attention}.`, 'error')
      } else if (result.obsolete > 0) {
        toast.show(`Отправлено: ${result.succeeded}. Отклонено сервером как устаревшие: ${result.obsolete}.`, 'info')
      } else if (result.deferred > 0) {
        toast.show(`Отправлено: ${result.succeeded}. Повторим автоматически: ${result.deferred}.`, 'info')
      } else if (result.succeeded > 0) {
        toast.show(`Отправлено изменений: ${result.succeeded}.`, 'success')
      } else {
        toast.show('Новых изменений для отправки нет.', 'info')
      }
    } catch {
      toast.show('Изменения не отправлены. Проверьте подключение и повторите.', 'error')
    } finally {
      setManualSyncing(false)
    }
  }

  async function retry(item: OutboxRecord) {
    try {
      const retried = await retryOutboxItem(item.id)
      await sync.refreshQueue()
      if (!retried) {
        toast.show('Действие уже обработано в другой вкладке. Очередь обновлена.', 'info')
        return
      }
      toast.show('Действие снова готово к отправке.', 'success')
      if (sync.browserOnline) await sendSavedChanges()
    } catch (caught) {
      toast.show(errorMessage(caught), 'error')
    }
  }

  async function confirmDiscard() {
    if (!discardCandidate) return
    const item = discardCandidate
    setDiscardingId(item.id)
    try {
      const result = await discardOutboxItem(item.id)
      await sync.refreshQueue()
      if (result === 'active') {
        toast.show('Отправка уже началась. Дождитесь результата — удалить действие во время отправки нельзя.', 'info')
      } else if (result === 'missing') {
        toast.show('Действие уже обработано в другой вкладке. Список обновлён.', 'info')
      } else {
        toast.show('Неотправленное действие удалено с устройства.', 'info')
      }
      setDiscardCandidate(null)
    } catch {
      toast.show('Не удалось удалить сохранённое действие. Оно осталось на устройстве — повторите удаление.', 'error')
    } finally {
      setDiscardingId(null)
    }
  }

  return <div className="page-stack">
    <section className="page-heading"><div><p className="eyebrow">Автономная работа</p><h2>Сохранённые изменения</h2><p>Работайте без сети: изменения останутся на устройстве и отправятся, когда появится подключение.</p></div><Button onClick={() => void sendSavedChanges()} loading={sync.syncing || manualSyncing} disabled={!sync.browserOnline || sync.pendingCount === 0}><RefreshCw size={17} />Отправить сейчас</Button></section>

    <section className="sync-overview">
      <article className={`connection-card ${sync.browserOnline ? 'connection-card--online' : 'connection-card--offline'}`}><span>{sync.browserOnline ? <Wifi /> : <CloudOff />}</span><div><p>Подключение</p><strong>{sync.browserOnline ? 'Есть подключение' : 'Офлайн-режим включён'}</strong><small>{sync.browserOnline ? 'Сохранённые изменения можно отправить' : 'Можно продолжать работу с доступными данными'}</small></div></article>
      <article className="connection-card"><span><Cloud /></span><div><p>Ожидают отправки</p><strong>{formatNumber(sync.pendingCount)}</strong><small>{sync.pendingCount ? 'порядок действий будет сохранён' : 'все действия переданы'}</small></div></article>
      <article className={sync.attentionCount ? 'connection-card connection-card--attention' : 'connection-card'}><span><AlertOctagon /></span><div><p>Требуют решения</p><strong>{formatNumber(sync.attentionCount)}</strong><small>{sync.attentionCount ? 'проверьте конфликты и отклонения' : 'конфликтов нет'}</small></div></article>
      <article className={shell === 'ready' ? 'connection-card connection-card--online' : shell === 'missing' ? 'connection-card connection-card--attention' : 'connection-card'} data-testid="offline-shell"><span><Download /></span><div><p>Приложение без сети</p><strong>{shell === 'ready' ? 'Сохранено' : shell === 'missing' ? 'Не сохранено' : shell === 'unsupported' ? 'Недоступно' : '—'}</strong><small>{shell === 'ready' ? 'откроется без подключения' : shell === 'missing' ? 'откройте приложение при подключении к интернету и подождите несколько секунд' : shell === 'unsupported' ? 'браузер не поддерживает работу без сети' : 'проверяем'}</small></div></article>
      <article className="connection-card" data-testid="obsolete-card"><span><Ban /></span><div><p>Отклонено сервером</p><strong>{formatNumber(sync.obsoleteCount)}</strong><small>{sync.obsoleteCount ? 'работа уже завершена или изменена: действия устарели' : 'устаревших действий нет'}</small></div></article>
      <article className="connection-card"><span><Database /></span><div><p>Сохранено на устройстве</p><strong>{usage ? `${formatNumber(usage.usage / 1024 / 1024, 1)} МБ` : '—'}</strong><small>{usage?.quota ? `можно сохранить ещё до ${formatNumber(usage.quota / 1024 / 1024 / 1024, 1)} ГБ` : 'доступный объём уточняется автоматически'}</small></div></article>
    </section>

    <section className="surface surface--flush">
      <div className="section-heading section-heading--padded"><div><h3>Неотправленные действия</h3><p>{sync.lastCompletedAt ? `Последняя проверка ${formatRelativeTime(sync.lastCompletedAt)}.` : 'Проверка начнётся автоматически после подключения.'}</p></div></div>
      {activeQueue.length === 0 ? <EmptyState icon={<Cloud />} title="Всё синхронизировано" description="На устройстве нет действий, ожидающих отправки." /> : <div className="sync-list">{activeQueue.map((item) => <SyncItem key={item.id} item={item} onRetry={() => void retry(item)} onResolve={() => setResolving(item)} onDiscard={() => setDiscardCandidate(item)} />)}</div>}
    </section>

    {obsoleteQueue.length > 0 && <section className="surface surface--flush" aria-label="Отклонённые сервером действия">
      <div className="section-heading section-heading--padded"><div><h3>Отклонено сервером</h3><p>Эти действия сервер не принял насовсем: работа к моменту отправки уже была завершена или отменена, либо данные изменились. Они не мешают отправке остальных изменений. Проверьте задание в списке заданий и удалите устаревшие записи.</p></div><Button variant="secondary" loading={clearingObsolete} onClick={() => void clearObsolete()}><Trash2 size={16} />Удалить все отклонённые</Button></div>
      <div className="sync-list">{obsoleteQueue.map((item) => <SyncItem key={item.id} item={item} onRetry={() => void retry(item)} onResolve={() => setResolving(item)} onDiscard={() => setDiscardCandidate(item)} />)}</div>
    </section>}

    <section className="surface" aria-label="Диагностика устройства">
      <div className="section-heading"><div><h3>Диагностика</h3><p>Приложение само отправляет администратору отчёт о состоянии очереди и версии. Если что-то не отправляется, нажмите кнопку — администратор увидит очередь этого устройства и сможет очистить её удалённо.</p></div><Button variant="secondary" loading={reporting} disabled={!sync.browserOnline} onClick={() => void sendStatusReport()}><Activity size={16} />Отправить отчёт о состоянии</Button></div>
      <dl className="summary-facts"><div><dt>Версия приложения</dt><dd>{APP_BUILD}</dd></div><div><dt>Офлайн-оболочка</dt><dd>{shellBuild ?? '—'}</dd></div><div><dt>Устройство</dt><dd>{deviceId().slice(0, 8)}</dd></div><div><dt>Отчёт отправлен</dt><dd>{sync.lastReportAt ? formatRelativeTime(sync.lastReportAt) : 'ещё нет'}</dd></div></dl>
    </section>

    <section className="install-card"><span className="install-card__icon"><Download /></span><div><h3>{installed ? 'Приложение установлено' : 'Установить на устройство'}</h3><p>{installed ? 'Работы, которые вы уже открывали, доступны даже без подключения.' : 'Установленная версия быстрее открывается и остаётся доступной при нестабильном подключении.'}</p></div>{!installed && installPrompt && <Button variant="secondary" onClick={() => void install()} loading={installing}>Установить</Button>}{!installed && !installPrompt && <span className="install-hint">{installHint(navigator.userAgent)}</span>}</section>
    {resolving && <EquipmentConflictModal record={resolving} onClose={() => setResolving(null)} onResolved={async () => { await sync.refreshQueue(); await sendSavedChanges() }} />}
    <ConfirmDialog
      open={Boolean(discardCandidate)}
      title="Удалить сохранённое действие?"
      description={discardCandidate?.fileBlob ? 'Файл и неотправленное действие будут удалены только с этого устройства. Восстановить их после удаления нельзя.' : 'Неотправленное действие будет удалено только с этого устройства. Восстановить его после удаления нельзя.'}
      confirmLabel="Удалить с устройства"
      tone="danger"
      loading={Boolean(discardCandidate && discardingId === discardCandidate.id)}
      onClose={() => setDiscardCandidate(null)}
      onConfirm={() => void confirmDiscard()}
    />
  </div>
}

export function SyncItem({ item, onRetry, onResolve, onDiscard }: { item: OutboxRecord; onRetry(): void; onResolve(): void; onDiscard(): void }) {
  const labels: Record<OutboxRecord['state'], string> = { queued: 'Ждёт отправки', syncing: 'Отправляется', retry: item.attempts >= 8 ? 'Не удаётся отправить, повторяем' : 'Повторим автоматически', 'auth-required': 'Нужно войти снова', conflict: 'Нужно выбрать версию', failed: 'Нужно проверить', obsolete: 'Отклонено сервером' }
  const equipmentConflict = item.state === 'conflict' && item.method === 'PATCH' && /^\/equipment\/[^/]+$/.test(item.path)
  const sourceLink = item.path.startsWith('/import/ppr/')
    ? `/imports/${item.path.split('/')[3]}`
    : item.path === '/import/ppr'
      ? '/imports'
      : item.path.startsWith('/equipment')
        ? '/equipment'
        : item.path.startsWith('/plans/')
          ? '/plans'
          : null
  return <article className="sync-item">
    <span className="sync-item__icon">{item.state === 'obsolete' ? <Ban /> : item.fileBlob ? <FileUp /> : ['auth-required', 'conflict', 'failed'].includes(item.state) ? <AlertOctagon /> : <RotateCcw />}</span>
    <div className="sync-item__body"><div><strong>{item.label}</strong><Badge tone={statusTone(item.state)}>{labels[item.state]}</Badge></div><p>Сохранено {formatDate(item.createdAt, true)}{item.fileBlob ? ` · ${formatNumber((item.fileSize ?? item.fileBlob.size) / 1024 / 1024, 1)} МБ` : ''}</p>{item.lastError && <small className="text-danger">{item.lastError}</small>}{item.state === 'retry' && <small>Следующая попытка {formatRelativeTime(item.nextAttemptAt)}</small>}{item.state === 'syncing' && <small>Удаление станет доступно после завершения отправки.</small>}</div>
    <div className="sync-item__actions">{(item.state === 'failed' || item.state === 'obsolete' || (item.state === 'conflict' && !equipmentConflict)) && <Button variant="secondary" onClick={onRetry}><RotateCcw size={16} />Повторить отправку</Button>}{equipmentConflict && <Button variant="secondary" onClick={onResolve}><GitMerge size={16} />Сопоставить изменения</Button>}{item.state === 'conflict' && !equipmentConflict && sourceLink && <Link className="button button--secondary" to={sourceLink}>Открыть исходные данные</Link>}<Button variant="quiet" onClick={onDiscard} disabled={item.state === 'syncing'} aria-label={`Удалить: ${item.label}`}><Trash2 size={16} />Удалить</Button></div>
  </article>
}

type ConflictFieldKey = 'name' | 'externalKey' | 'unit' | 'quantity' | 'active' | 'systemId' | 'typeId'
type ConflictChoice = 'server' | 'local'

interface ConflictField {
  key: ConflictFieldKey
  label: string
  serverValue: unknown
  serverDisplay?: string
}

function conflictFields(equipment: Equipment): ConflictField[] {
  return [
    { key: 'name', label: 'Название', serverValue: equipment.name },
    { key: 'externalKey', label: 'Код', serverValue: equipment.externalKey ?? equipment.code ?? null },
    { key: 'unit', label: 'Единица', serverValue: equipment.unit ?? null },
    { key: 'quantity', label: 'Количество', serverValue: equipment.quantity ?? null },
    { key: 'active', label: 'Состояние', serverValue: equipment.active ?? equipment.status !== 'INACTIVE' },
    { key: 'systemId', label: 'Система', serverValue: equipment.system?.id ?? null, serverDisplay: equipment.system?.name },
    { key: 'typeId', label: 'Тип', serverValue: equipment.type?.id ?? null, serverDisplay: equipment.type?.name },
  ]
}

function sameConflictValue(left: unknown, right: unknown): boolean {
  return JSON.stringify(left ?? null) === JSON.stringify(right ?? null)
}

function conflictValue(value: unknown, display?: string): string {
  if (display) return display
  if (value === true) return 'В работе'
  if (value === false) return 'Неактивно'
  if (value === null || value === undefined || value === '') return 'Не указано'
  return String(value)
}

export function EquipmentConflictModal({ record, onClose, onResolved }: { record: OutboxRecord; onClose(): void; onResolved(): Promise<void> }) {
  const toast = useToast()
  const pending = useMemo(
    () => record.jsonBody && typeof record.jsonBody === 'object' ? record.jsonBody as Record<string, unknown> : {},
    [record.jsonBody],
  )
  const [latest, setLatest] = useState<Equipment | null>(null)
  const [choices, setChoices] = useState<Partial<Record<ConflictFieldKey, ConflictChoice>>>({})
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [reload, setReload] = useState(0)

  useEffect(() => {
    let cancelled = false
    async function loadLatest() {
      try {
        if (!navigator.onLine) throw new Error('Подключитесь к сети, чтобы получить текущие значения оборудования.')
        const equipment = normalizeEquipment(await apiRequest<unknown>(record.path))
        if (!equipment.id || !Number.isInteger(equipment.revision)) throw new Error('Не удалось получить актуальные данные оборудования.')
        if (!cancelled) setLatest(equipment)
      } catch (caught) {
        if (!cancelled) setError(errorMessage(caught))
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void loadLatest()
    return () => { cancelled = true }
  }, [record.path, reload])

  const fields = useMemo(() => latest ? conflictFields(latest).map((field) => ({
    ...field,
    localValue: pending[field.key],
    same: sameConflictValue(field.serverValue, pending[field.key]),
  })) : [], [latest, pending])
  const differing = fields.filter((field) => !field.same)
  const ready = Boolean(latest && differing.every((field) => choices[field.key]))

  function reloadLatest() {
    setLatest(null)
    setChoices({})
    setError('')
    setLoading(true)
    setReload((current) => current + 1)
  }

  function chooseAllLocal() {
    setChoices(Object.fromEntries(differing.map((field) => [field.key, 'local'])) as Partial<Record<ConflictFieldKey, ConflictChoice>>)
  }

  async function save() {
    if (!latest || !ready) return
    setSaving(true)
    setError('')
    try {
      const merged = Object.fromEntries(fields.map((field) => [
        field.key,
        field.same || choices[field.key] === 'server' ? field.serverValue : field.localValue,
      ]))
      const resolved = await resolveEquipmentConflict(record.id, latest.revision, merged)
      if (!resolved) throw new Error('Сохранённое изменение больше не найдено')
      toast.show('Выбранные значения сохранены и готовы к отправке.', 'success')
      onClose()
      await onResolved()
    } catch (caught) {
      setError(errorMessage(caught))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal open onClose={onClose} title="Сопоставить изменения оборудования" description="В системе уже сохранена более новая версия. Выберите значение для каждого отличающегося поля — до подтверждения данные не изменятся." size="large">
      <div className="modal__body form-stack">
        {loading && <LoadingState label="Получаем текущие значения оборудования" />}
        {error && <div className="form-message form-message--error" role="alert">{error}</div>}
        {!loading && error && <Button variant="secondary" onClick={reloadLatest}><RefreshCw size={16} />Получить значения снова</Button>}
        {latest && <>
          <div className="conflict-toolbar"><div><strong>{latest.name}</strong><small>Текущая версия: {latest.revision}</small></div>{differing.length > 0 && <Button variant="secondary" onClick={chooseAllLocal}>Выбрать все сохранённые</Button>}</div>
          {differing.length === 0 ? <div className="form-message form-message--warning">Значения уже совпадают. Подтвердите актуальную версию, чтобы продолжить отправку.</div> : <div className="responsive-table conflict-table"><table><thead><tr><th>Поле</th><th>Сохранено в системе</th><th>Сохранено на устройстве</th><th>Что оставить</th></tr></thead><tbody>{differing.map((field) => <tr key={field.key}><td data-label="Поле"><strong>{field.label}</strong></td><td data-label="Сохранено в системе">{conflictValue(field.serverValue, field.serverDisplay)}</td><td data-label="Сохранено на устройстве">{conflictValue(field.localValue)}</td><td data-label="Что оставить"><select name={`conflictChoice-${field.key}`} aria-label={`Что оставить: ${field.label}`} value={choices[field.key] ?? ''} onChange={(event) => setChoices((current) => ({ ...current, [field.key]: event.target.value as ConflictChoice }))}><option value="">Выберите</option><option value="server">Значение в системе</option><option value="local">Сохранённое на устройстве</option></select></td></tr>)}</tbody></table></div>}
        </>}
      </div>
      <div className="modal__footer"><Button variant="quiet" onClick={onClose}>Отмена</Button><Button onClick={() => void save()} loading={saving} disabled={!ready}>Подтвердить и отправить</Button></div>
    </Modal>
  )
}
